import { useMemo } from "react";
import { Card /* , OverlayTrigger, Tooltip */ } from "react-bootstrap";
import { useIntl, FormattedMessage } from "react-intl";
import { useNavigate, Navigate } from "react-router-dom";
import { Row } from "react-table";

import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import DataTable from "components/organisms/DataTable/DataTable";
import ColumnFilter from "components/organisms/DataTable/components/ColumnFilter";
import usePermissions from "hooks/usePermissions";
import { useGetRateCardList } from "api/queries/rate-card/useGetRateCardList";
import useTableQueryRequest from "hooks/useTableQueryRequest";
import Filter from "components/organisms/Filter/Filter";
import { getDateParams } from "helpers/commonHelpers";
// import { ReactComponent as ExportIcon } from "assets/images/icons/TopNavIcons/ExportIcon.svg";
import useRateCardFilters from "hooks/filters/useRateCardFilters";
import getRateCardColumns from "./RateCardColumns";

function RateCards() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const { additionalColumns, filterConfig, getHiddenColumns } = useRateCardFilters();

  const columns = useMemo(() => getRateCardColumns(intl), []);

  const { instance, loading, totalData } = useTableQueryRequest(useGetRateCardList, columns, {
    hiddenColumns: getHiddenColumns(),
    params: (tableInstance: any) => ({
      properties: additionalColumns.join(","),
      ...(tableInstance.state.globalFilter?.query?.length && {
        active_columns: tableInstance.visibleColumns.map((column: any) => column.id).join(","),
      }),
    }),
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      ...(globalFilter.provider?.length && { provider: globalFilter.provider }),
      ...(globalFilter.freight_mode?.length && { freight_modes: globalFilter.freight_mode.join(",") }),
      ...(globalFilter.start_date?.type && getDateParams(globalFilter.start_date, "start_date")),
      ...(globalFilter.expiry_date?.type && getDateParams(globalFilter.expiry_date, "expiry_date")),
      ...(globalFilter.lane_origin?.length && { lane_origin: globalFilter.lane_origin }),
      ...(globalFilter.lane_destination?.length && { lane_destination: globalFilter.lane_destination }),
      ...(globalFilter.is_active?.length === 1 && { is_active: globalFilter.is_active[0] }),
      ...(globalFilter.dangerous_goods?.length === 1 && { dangerous_goods: globalFilter.dangerous_goods[0] }),
      ...(globalFilter.incoterms?.length && { incoterms: globalFilter.incoterms.join(",") }),
      ...(globalFilter.service_requirement?.length && { service_requirement: globalFilter.service_requirement.join(",") }),
    }),
  });

  const handleRowClick = (row: Row<any>) => {
    navigate(`/settings/rate-card/${row.original?.id}`);
  };

  if (!JSON.parse(permissions || "{}")?.permissions?.admin_settings?.view_rate_card) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <Card body className="shadow-none quote-container">
      <div className="d-flex flex-wrap align-items-center mb-4">
        <h3 className="mr-3">
          <FormattedMessage id="settings.rate-cards.rateCard" defaultMessage="Rate Card" />
        </h3>
        <GlobalTableSearch
          currentFilters={instance.state.globalFilter}
          setFilters={instance.setGlobalFilter}
          filterKey="query"
          eventContext="Quote Request List"
        />
      </div>
      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={loading}
        onRowClick={handleRowClick}
        headerRight={
          <div className="d-flex align-items-center">
            <Filter
              eventContext="Rate Card List"
              currentFilters={instance.state.globalFilter}
              setFilters={instance.setGlobalFilter}
              config={filterConfig}
            />
            <div className="ml-3">
              <ColumnFilter columns={instance.allColumns} eventContext="Rate Card List" listViewName="rate_card" />
            </div>
            {/* <div className="ml-3">
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 0 }}
                overlay={
                  <Tooltip id="">
                    <FormattedMessage id="components.molecules.navigation.export" defaultMessage="Export" />
                  </Tooltip>
                }
              >
                <div>
                  <ExportIcon style={{ cursor: "pointer" }} />
                </div>
              </OverlayTrigger>
            </div> */}
          </div>
        }
        withoutBody
      />
    </Card>
  );
}

export default RateCards;
